import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DocumentJobAdminService } from 'src/app/shared/services/document-job-admin.service';
import {
  AdminDocumentJob,
  DocumentJobCompany,
  DocumentJobFilters,
  DocumentJobStatus,
  DocumentJobType,
  PaginationParams,
} from 'src/app/admin/models/document-job.model';

@Component({
  selector: 'app-list-document-jobs',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    ToastModule,
    SelectModule,
    TooltipModule,
  ],
  templateUrl: './list-document-jobs.component.html',
})
export class ListDocumentJobsComponent implements OnInit {
  private jobService = inject(DocumentJobAdminService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);

  jobs: AdminDocumentJob[] = [];
  companies: DocumentJobCompany[] = [];
  loading = false;

  DocumentJobStatus = DocumentJobStatus;
  DocumentJobType = DocumentJobType;

  statusFilter: DocumentJobStatus | null = null;
  typeFilter: DocumentJobType | null = null;
  companyFilter: string | null = null;

  currentPage = 1;
  pageSize = 20;
  totalRecords = 0;
  totalPages = 0;

  statusOptions = [
    { label: 'All', value: null },
    { label: 'Pending', value: DocumentJobStatus.PENDING },
    { label: 'Processing', value: DocumentJobStatus.PROCESSING },
    { label: 'Completed', value: DocumentJobStatus.COMPLETED },
    { label: 'Failed', value: DocumentJobStatus.FAILED },
  ];

  typeOptions = [
    { label: 'All', value: null },
    { label: 'Single (sync)', value: DocumentJobType.SINGLE },
    { label: 'Batch (async)', value: DocumentJobType.BATCH },
  ];

  companyOptions: { label: string; value: string | null }[] = [{ label: 'All', value: null }];

  pageSizeOptions = [
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  ngOnInit(): void {
    this.loadCompanies();
    this.loadJobs();
  }

  loadCompanies(): void {
    this.jobService.listCompanies().subscribe({
      next: (response) => {
        this.companies = response.data;
        this.companyOptions = [
          { label: 'All', value: null },
          ...response.data.map((c) => ({ label: c.name, value: c.id })),
        ];
      },
      error: () => {
        // Non-blocking: the filter just stays on "All".
      },
    });
  }

  loadJobs(): void {
    this.loading = true;

    const filters: DocumentJobFilters = {};
    if (this.statusFilter) filters.status = this.statusFilter;
    if (this.typeFilter) filters.type = this.typeFilter;
    if (this.companyFilter) filters.companyId = this.companyFilter;

    const pagination: PaginationParams = { page: this.currentPage, limit: this.pageSize };

    this.jobService.listJobs(filters, pagination).subscribe({
      next: (response) => {
        this.jobs = response.data;
        this.totalRecords = response.pagination.total;
        this.totalPages = response.pagination.totalPages;
        this.loading = false;
      },
      error: () => {
        this.notificationService.error('Failed to load document jobs');
        this.loading = false;
      },
    });
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadJobs();
  }

  resetFilters(): void {
    this.statusFilter = null;
    this.typeFilter = null;
    this.companyFilter = null;
    this.currentPage = 1;
    this.loadJobs();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadJobs();
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.loadJobs();
  }

  viewDetails(jobId: string): void {
    this.router.navigate(['/admin/document-jobs', jobId]);
  }

  getJobType(job: AdminDocumentJob): DocumentJobType {
    return job.batchId.startsWith('single-') ? DocumentJobType.SINGLE : DocumentJobType.BATCH;
  }

  getStatusClasses(status: DocumentJobStatus): string {
    const classes: Record<DocumentJobStatus, string> = {
      [DocumentJobStatus.PENDING]: 'bg-greyscale-100 text-greyscale-700',
      [DocumentJobStatus.PROCESSING]: 'bg-primary-100 text-primary-700',
      [DocumentJobStatus.COMPLETED]: 'bg-validation-100 text-validation-700',
      [DocumentJobStatus.FAILED]: 'bg-error-100 text-error-700',
    };
    return classes[status] || 'bg-greyscale-100 text-greyscale-700';
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
